import { createSlice } from "@reduxjs/toolkit";
import { initialState } from "../initialState.js";
import {
  getNotificationById,
  getNotificationsList,
  getUnreadNotificationsCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "./operations.js";

const notificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    ...initialState.notifications,
  },
  reducers: {
    addLiveNotification(state, action) {
      state.notificationsList.unshift(action.payload);
      state.unreadCount += 1;
      state.toasts.push(action.payload);
    },
    removeToast(state, action) {
      state.toasts = state.toasts.filter(t => t.id !== action.payload);
    },
    openPopup(state) {
      state.isPopupOpen = true;
    },
    closePopup(state) {
      state.isPopupOpen = false;
    },
    clearDetailedNotification(state) {
      state.detailedNotification = null;
    },
  },
  extraReducers: builder =>
    builder

      // Get notifications list
      .addCase(getNotificationsList.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getNotificationsList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notificationsList = action.payload.items ?? action.payload;
      })
      .addCase(getNotificationsList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Get notification by ID +
      .addCase(getNotificationById.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getNotificationById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.detailedNotification = action.payload;
      })
      .addCase(getNotificationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Get unread notifications count
      .addCase(getUnreadNotificationsCount.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUnreadNotificationsCount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.unreadCount = action.payload.unread_count;
      })
      .addCase(getUnreadNotificationsCount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Mark notification as read
      .addCase(markNotificationAsRead.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedNotificationId = action.meta.arg;
        const index = state.notificationsList.findIndex(
          notification => notification.id === updatedNotificationId
        );
        if (index !== -1) {
          state.notificationsList[index].is_read = true;
        }
        if (state.unreadCount > 0) {
          state.unreadCount -= 1;
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Mark all notifications as read
      .addCase(markAllNotificationsAsRead.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, state => {
        state.isLoading = false;
        state.notificationsList = state.notificationsList.map(notification => ({
          ...notification,
          is_read: true,
        }));
        state.unreadCount = 0;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      }),
});

export const {
  addLiveNotification,
  removeToast,
  openPopup,
  closePopup,
  clearDetailedNotification,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
